import { useCallback, useEffect, useRef, useState, RefObject } from "react";
import { calculateConstrainedPan } from "../utils/zoomMath";

const PAN_KEYS = [
  "arrowleft",
  "arrowright",
  "arrowup",
  "arrowdown",
  "a",
  "d",
  "w",
  "s",
];

const DEFAULT_PAN_STEP_PERCENT = 5;

interface UseKeyboardZoomParams {
  containerRef: RefObject<HTMLElement>;
  isZoomed: boolean;
  currentScaleRef: { current: number };
  positionXRef: { current: number };
  positionYRef: { current: number };
  contentWidth: number;
  contentHeight: number;
  onClickZoom: (position: { clientX: number; clientY: number }) => void;
  onPan: (x: number, y: number) => void;
  panStepPercent?: number;
}

interface UseKeyboardZoomReturn {
  handleKeyDown: (event: React.KeyboardEvent) => void;
  handleKeyUp: (event: React.KeyboardEvent) => void;
  handleZoomClick: () => void;
  isFocused: boolean;
}

export default function useKeyboardZoom({
  containerRef,
  isZoomed,
  currentScaleRef,
  positionXRef,
  positionYRef,
  contentWidth,
  contentHeight,
  onClickZoom,
  onPan,
  panStepPercent = DEFAULT_PAN_STEP_PERCENT,
}: UseKeyboardZoomParams): UseKeyboardZoomReturn {
  const [isFocused, setIsFocused] = useState(false);
  const activeKeysRef = useRef<Record<string, boolean>>({});
  const getContainerCenter = useCallback((): {
    clientX: number;
    clientY: number;
  } => {
    const container = containerRef.current;
    if (!container) {
      return { clientX: 0, clientY: 0 };
    }
    const { top, left, width, height } = container.getBoundingClientRect();
    return {
      clientX: left + width / 2,
      clientY: top + height / 2,
    };
  }, [containerRef]);

  const handleZoomClick = useCallback((): void => {
    const center = getContainerCenter();
    onClickZoom(center);
  }, [getContainerCenter, onClickZoom]);

  const applyPan = useCallback(
    (keys: string[]): void => {
      if (!isZoomed) {
        return;
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      const scale = currentScaleRef.current;
      const { width: containerWidth, height: containerHeight } =
        container.getBoundingClientRect();

      let deltaX = 0;
      let deltaY = 0;

      if (keys.includes("arrowleft") || keys.includes("a")) {
        deltaX = -panStepPercent;
      }
      if (keys.includes("arrowright") || keys.includes("d")) {
        deltaX = panStepPercent;
      }
      if (keys.includes("arrowup") || keys.includes("w")) {
        deltaY = -panStepPercent;
      }
      if (keys.includes("arrowdown") || keys.includes("s")) {
        deltaY = panStepPercent;
      }

      const { x: newX, y: newY } = calculateConstrainedPan({
        containerWidth,
        containerHeight,
        contentWidth,
        contentHeight,
        scale,
        currentX: positionXRef.current,
        currentY: positionYRef.current,
        deltaX,
        deltaY,
      });

      onPan(newX, newY);
    },
    [
      isZoomed,
      containerRef,
      currentScaleRef,
      positionXRef,
      positionYRef,
      contentWidth,
      contentHeight,
      panStepPercent,
      onPan,
    ],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      const keyLowerCase = event.key.toLowerCase();
      activeKeysRef.current[keyLowerCase] = true;

      if (PAN_KEYS.includes(keyLowerCase)) {
        event.preventDefault();

        const extraKeys = Object.keys(activeKeysRef.current).filter(
          (key) => activeKeysRef.current[key] && !PAN_KEYS.includes(key),
        );

        if (extraKeys.length > 0) {
          Object.keys(activeKeysRef.current).forEach((key) => {
            if (keyLowerCase !== key && PAN_KEYS.includes(key)) {
              activeKeysRef.current[key] = false;
            }
          });
        }

        const activePanKeys = Object.keys(activeKeysRef.current).filter(
          (key) => activeKeysRef.current[key] && PAN_KEYS.includes(key),
        );

        applyPan(activePanKeys);
      }
    },
    [applyPan],
  );

  const handleKeyUp = useCallback((event: React.KeyboardEvent): void => {
    const keyLowerCase = event.key.toLowerCase();
    activeKeysRef.current[keyLowerCase] = false;

    if (PAN_KEYS.includes(keyLowerCase)) {
      Object.keys(activeKeysRef.current).forEach((key) => {
        if (PAN_KEYS.includes(key)) {
          activeKeysRef.current[key] = false;
        }
      });
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleFocus = (): void => setIsFocused(true);
    const handleBlur = (): void => {
      setIsFocused(false);
      activeKeysRef.current = {};
    };

    container.addEventListener("focusin", handleFocus);
    container.addEventListener("focusout", handleBlur);

    return () => {
      container.removeEventListener("focusin", handleFocus);
      container.removeEventListener("focusout", handleBlur);
    };
  }, [containerRef]);

  return {
    handleKeyDown,
    handleKeyUp,
    handleZoomClick,
    isFocused,
  };
}
